// CheckoutContext.tsx

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback
} from "react";

export interface Coupon {
  id: string;
  client_id: string;
  active: boolean;
  affiliate_id: string;
  discount_type: number;
  discount_value: string;
  limit_date: Date;
  limit_quantity: number;
  times_used: number;
  min_purchase_value: string;
  product_limit: number;
  name: string;
}

interface CouponState {
  name: string;
  found: Coupon | null;
  message: string;
  show: boolean;
}

// Define the initial state for your context
interface CheckoutState {
  cart: any[];
  card: {
    holder_name: string,
    expiration_date: string,
    expiration_year: string,
    card_number: string,
    security_code: string,
    installment: number,
  };
  setCard: Function;
  planId: string;
  saleId: string;
  setPlanId: Function;
  planName: string;
  setPlanName: Function;
  addToCart: Function;
  cleanCart: Function;
  purchaser: any;
  address: {
    logradouro: string
    bairro: string
    cidade: string
    estado: string
    cep: string
    complemento?: string
    numero: string
  };
  setPurchaserData: Function;
  setAddressData: Function;
  totalPrice: number;
  setTotalPrice: Function;
  clientAndAffiliate: object;
  setClientAndAffiliate: Function;
  paymentType: string;
  setPaymentType: Function;
  coupon: CouponState;
  setCoupon: Function;
  firstLoad: boolean;
  setFirstLoad: Function;
  paytimeInfo: object;
  setPaytimeInfo: Function;
  resetContext: Function;
  setSaleId: Function;
  abandonedCartId: string;
  setAbandonedCartId: Function;
  // Add other relevant state properties
}

// Create the context
const CheckoutContext = createContext<CheckoutState | undefined>(undefined);

// Create a custom hook for using the context
const useCheckout = (): CheckoutState => {
  const context = useContext(CheckoutContext);
  if (!context) {
    throw new Error("useCheckout must be used within a CheckoutProvider");
  }
  return context;
};

// Define a provider component to wrap your app
interface CheckoutProviderProps {
  children: ReactNode;
}

const CheckoutProvider: React.FC<CheckoutProviderProps> = ({ children }) => {
  const [state, setState] = useState<CheckoutState>({
    cart: [],
    addToCart: () => null,
    cleanCart: () => null,
    purchaser: {
      document_number: "",
      telephone: "",
      name: "",
      lastname: "",
      email: "",
    },
    address: {
      cep: "",
      logradouro: "",
      cidade: "",
      complemento: "",
      numero: "",
      bairro: "",
      estado: "",
    },
    setPurchaserData: () => null,
    setAddressData: () => null,
    totalPrice: 0,
    setTotalPrice: () => null,
    setCard: () => null,
    card: {
      holder_name: '',
      card_number: '',
      security_code: '',
      expiration_date: '',
    },
    clientAndAffiliate: {},
    setClientAndAffiliate: () => null,
    paymentType: "Cartão",
    setPaymentType: () => null,
    coupon: {
      name: "",
      found: null,
      message: "",
      show: false,
    },
    setCoupon: () => null,
    planId: "",
    setPlanId: () => null,
    planName: "",
    setPlanName: () => null,
    setShowDiscount: () => null,
    firstLoad: true,
    setFirstLoad: () => null,
    paytimeInfo: {
      store_id: "",
    },
    saleId: "",
    abandonedCartId: "",
    setAbandonedCartId: () => null,
    setPaytimeInfo: () => null,
    resetContext: () => null,
  });

  const resetContext = () => {
    setState((prevState) => ({ ...prevState, cart: [],
      purchaser: {
        document_number: "",
        telephone: "",
        name: "",
        lastname: "",
        email: "",
      },
      address: {
        cep: "",
        logradouro: "",
        cidade: "",
        complemento: "",
        numero: "",
        bairro: "",
        estado: "",
      },
      totalPrice: 0,
      card: {
        holder_name: '',
        card_number: '',
        security_code: '',
        expiration_date: '',
      },
      clientAndAffiliate: {},
      paymentType: "Cartão",
      coupon: {
        name: "",
        found: null,
        message: "",
        show: false,
      },
      planId: "",
      planName: "",
      saleId: "",
      abandonedCartId: "",
      firstLoad: true,
      paytimeInfo: {
        store_id: "",
      },
  }))
}

  // You can define functions to update the state as needed
  const addToCart = (item: object) => {
    const oldObject = state.cart.find((prod) => prod.id === item.id);
    if (oldObject) {
      const newCard: {}[] = state.cart.reduce((acc: {}[], product) => {
        if (product.id === item.id && item.quantity === 0) return acc;
        if (product.id === item.id)
          return [...acc, { ...product, quantity: item.quantity }];
        return [...acc, product];
      }, []);

      setState({ ...state, cart: newCard });
    } else setState({ ...state, cart: [...state.cart, item] });
  };

  const cleanCart = useCallback(() =>
    setState((prevState) => ({ ...prevState, cart: [] })), []);
  const setPurchaserData = useCallback((data: object) =>
    setState((prevState) => ({ ...prevState, purchaser: data })), []);
  const setAddressData = useCallback((data: object) =>
    setState((prevState) => ({ ...prevState, address: data })), []);
  const setTotalPrice = useCallback((data: number) =>
    setState((prevState) => ({ ...prevState, totalPrice: data })), []);
  const setCard = useCallback((data: object) =>
    setState((prevState) => ({ ...prevState, card: data })), []);
  const setClientAndAffiliate = useCallback((data: object) =>
    setState((prevState) => ({ ...prevState, clientAndAffiliate: data })), []);
  const setPaymentType = useCallback((data: string) =>
    setState((prevState) => ({ ...prevState, paymentType: data })), []);
  const setCoupon = useCallback((data: CouponState) =>
    setState((prevState) => ({ ...prevState, coupon: data })), []);
  const setFirstLoad = useCallback((data: boolean) =>
    setState((prevState) => ({ ...prevState, firstLoad: data })), []);
  const setPaytimeInfo = useCallback((data: object) =>
    setState((prevState) => ({ ...prevState, paytimeInfo: data })), []);
  const setPlanId = useCallback((data: string) =>
    setState((prevState) => ({ ...prevState, planId: data })), []);
  const setPlanName = useCallback((data: string) =>
    setState((prevState) => ({ ...prevState, planName: data })), []);
  const setSaleId = useCallback((data: string) =>
    setState((prevState) => ({ ...prevState, saleId: data })), []);
  const setAbandonedCartId = useCallback((data: string) =>
    setState((prevState) => ({ ...prevState, abandonedCartId: data })), []);
  // Define any context values you want to expose
  const contextValue: CheckoutState = {
    ...state,
    addToCart,
    cleanCart,
    setPurchaserData,
    setAddressData,
    setTotalPrice,
    setCard,
    setClientAndAffiliate,
    setPaymentType,
    setCoupon,
    setFirstLoad,
    setPlanId,
    setPlanName,
    setSaleId,
    setPaytimeInfo,
    setAbandonedCartId,
    resetContext
  };

  return (
    <CheckoutContext.Provider value={contextValue}>
      {children}
    </CheckoutContext.Provider>
  );
};

export { CheckoutProvider, useCheckout };
